import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";

export default function EditClientDialog({ open, onOpenChange, client, onSaved }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", company: "", password: "", logo_path: "", contract_terms: "" });

  useEffect(() => {
    if (open && client) {
      // Fetch full client to get contract_terms (list endpoint may omit it)
      (async () => {
        try {
          const { data } = await api.get(`/clients/${client.id}`);
          setForm({
            email: data.email || "",
            name: data.name || "",
            company: data.company || "",
            password: "",
            logo_path: data.logo_path || "",
            contract_terms: data.contract_terms || "",
          });
        } catch {
          setForm({
            email: client.email || "",
            name: client.name || "",
            company: client.company || "",
            password: "",
            logo_path: client.logo_path || "",
            contract_terms: "",
          });
        }
      })();
    }
  }, [open, client]);

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/upload?client_id=${encodeURIComponent(client.id)}&kind=branding`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setForm((s) => ({ ...s, logo_path: data.path }));
    } catch {
      toast.error("Erro a carregar logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        company: form.company,
        email: form.email,
        logo_path: form.logo_path || null,
        contract_terms: form.contract_terms || "",
      };
      if (form.password) payload.password = form.password;
      await api.put(`/clients/${client.id}`, payload);
      toast.success("Cliente atualizado");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao atualizar");
    } finally {
      setSaving(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F9F6F0] border border-[#EAE3DB] rounded-2xl max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#1A1412]">Editar {client.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4" data-testid="edit-client-form">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Logo do cliente</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl border border-[#EAE3DB] bg-white flex items-center justify-center overflow-hidden">
                {form.logo_path ? (
                  <AuthImage path={form.logo_path} className="w-full h-full object-contain p-2" />
                ) : (
                  <span className="text-[10px] text-[#8B5E3C] uppercase tracking-widest">sem logo</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button" variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-9 px-4 text-[10px] uppercase tracking-widest"
                  data-testid="edit-client-upload-logo"
                >
                  <Upload className="w-3.5 h-3.5 mr-2" />
                  {uploading ? "A enviar…" : (form.logo_path ? "Trocar" : "Carregar")}
                </Button>
                {form.logo_path && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, logo_path: "" })}
                    className="text-[10px] text-[#8A2A22] hover:underline uppercase tracking-widest text-left"
                  >
                    Remover
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleLogo} />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Nome</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]" data-testid="edit-client-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Empresa</Label>
            <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]" data-testid="edit-client-company" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]" data-testid="edit-client-email" />
            <p className="mt-1 text-[10px] text-[#8B5E3C] font-light">O mesmo email pode ser usado em vários clientes.</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Nova palavra-passe (opcional)</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Deixe vazio para manter" className="mt-2 h-11 bg-white border-[#EAE3DB]"
              data-testid="edit-client-password" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Condições Contratuais</Label>
            <textarea
              value={form.contract_terms}
              onChange={(e) => setForm({ ...form, contract_terms: e.target.value })}
              placeholder="Nº posts/mês, redes incluídas, fee mensal, ad spend acordado, entregas criativas, prazos de aprovação, etc."
              rows={6}
              className="mt-2 w-full rounded-md border border-[#EAE3DB] bg-white p-3 text-sm text-[#1A1412] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#C07C5B]/40 resize-y notranslate"
              translate="no"
              data-testid="edit-client-contract-terms"
            />
            <p className="mt-1 text-[10px] text-[#8B5E3C] font-light uppercase tracking-widest">Visível para a equipa e para o cliente</p>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB]"
              data-testid="edit-client-cancel">Cancelar</Button>
            <Button type="submit" disabled={saving}
              className="rounded-full bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0]"
              data-testid="edit-client-save">
              {saving ? "A guardar…" : "Guardar alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
